import { IS_VISIBLE, IS_ACTIVE, ANIMATION, MEDIA_PLAYING, EVENT } from '../constants';
import { event } from '../lib/event';
import { animatePlayButtonOnResponsive, toggleToolbarOnEvents } from './media-controls-responsive';
import sendEvent from '../helpers/tracking/analytics-wrapper';

const mediaControls = (element: string): void => {
    const mediaCnt: HTMLDivElement | null = document.querySelector(element);

    if (mediaCnt) {
        const teaserVideo = mediaCnt.querySelector<HTMLVideoElement>('#teaser');

        if (mediaCnt.classList.contains('has-teaser') && teaserVideo) {
            const isVideoPlayable = setInterval(() => {
                if (teaserVideo.readyState === 4) {
                    clearInterval(isVideoPlayable);
                    attachMediaControlsEvents(mediaCnt, teaserVideo);
                    teaserVideo.muted = false;

                    sendEvent({
                        category: 'Profile',
                        action: 'Teaser',
                        label: 'Playable',
                    });
                }
            }, 200);
        }
    }
};

const attachMediaControlsEvents = (mediaCnt: HTMLDivElement, teaserVideo: HTMLVideoElement): void => {
    const profileCnt = document.querySelector<HTMLDivElement>('.js-profile');
    const mediaControlsCnt = mediaCnt.querySelector<HTMLDivElement>('.js-media-controls');
    const playBtn = document.querySelector<HTMLDivElement>('.js-media-controls__play');
    const closeBtn = mediaCnt.querySelector<HTMLDivElement>('.js-media-controls__close');
    const pauseBtn = mediaCnt.querySelector<HTMLDivElement>('.js-media-controls__pause');
    const muteBtn = mediaCnt.querySelector<HTMLDivElement>('.js-media-controls__mute');
    const trackPosition = mediaCnt.querySelector<HTMLInputElement>('.js-media-controls__track');
    const volumeSlider = mediaCnt.querySelector<HTMLInputElement>('.js-media-controls__volume');
    const styleVolume = document.querySelector<HTMLStyleElement>('#js-controls__volume');

    const videoDuration: number = teaserVideo.duration;
    let mutedVolume = 0;

    // Specifically for responsive profile media controls with custom events
    const playMediaEvent: Event = event(EVENT.MEDIA_PLAY);
    const pauseMediaEvent: Event = event(EVENT.MEDIA_PAUSE);
    const closeMediaEvent: Event = event(EVENT.MEDIA_CLOSE);
    animatePlayButtonOnResponsive();
    toggleToolbarOnEvents(mediaCnt, profileCnt);

    playBtn?.classList.add(IS_ACTIVE);

    playBtn?.addEventListener('click', () => {
        playVideo(mediaControlsCnt, teaserVideo, profileCnt).then(() => {
            toggleControls(mediaControlsCnt, [pauseBtn, muteBtn, volumeSlider, trackPosition]);
            playBtn.classList.remove(IS_ACTIVE);
            pauseBtn?.classList.add(IS_ACTIVE);
            mediaCnt.dispatchEvent(playMediaEvent);

            sendEvent({
                category: 'Profile',
                action: 'Teaser',
                label: 'Play',
            });
        });
    });

    teaserVideo.addEventListener('volumechange', () => {
        const currentVolume = teaserVideo.volume;

        if (!volumeSlider || !styleVolume) {
            return;
        }

        volumeSlider.value = (currentVolume * 100).toString();

        if (currentVolume === 0 || teaserVideo.muted) {
            muteBtn?.classList.add('media-controls__mute--muted');
            const width = '1px';
            const style = `input[type='range'].media-controls__volume{background-size:${width}, 100%}`;
            styleVolume.textContent = style;
        } else {
            muteBtn?.classList.remove('media-controls__mute--muted');
            const width = currentVolume === 0 ? '1px' : currentVolume * 100 + '%';
            const style = `input[type='range'].media-controls__volume{background-size:${width}, 100%}`;
            styleVolume.textContent = style;
        }

        sendEvent({
            category: 'Profile',
            action: 'Teaser',
            label: 'Volume change',
            value: currentVolume,
        });
    });

    teaserVideo.addEventListener('ended', () => {
        setTimeout(() => {
            resetVideo(mediaControlsCnt, teaserVideo, profileCnt, playBtn);
            if (trackPosition) {
                trackPosition.value = '0';
            }
        }, 1000);

        sendEvent({
            category: 'Profile',
            action: 'Teaser',
            label: 'Ended',
        });
    });

    teaserVideo.addEventListener('timeupdate', function () {
        if (!isNaN(this.duration)) {
            const completed: number = (this.currentTime / this.duration) * 100;
            updateVideoTrack(completed, trackPosition);
        }
    });

    closeBtn?.addEventListener('click', () => {
        resetVideo(mediaControlsCnt, teaserVideo, profileCnt, playBtn);
        mediaCnt.dispatchEvent(closeMediaEvent);

        if (trackPosition) {
            trackPosition.value = '0';
        }

        sendEvent({
            category: 'Profile',
            action: 'Teaser',
            label: 'Close',
        });
    });

    pauseBtn?.addEventListener('click', () => {
        pauseVideo(mediaControlsCnt, teaserVideo, profileCnt, playBtn);
        mediaCnt.dispatchEvent(pauseMediaEvent);

        sendEvent({
            category: 'Profile',
            action: 'Teaser',
            label: 'Pause',
        });
    });

    trackPosition?.addEventListener('input', () => {
        scrollVideo(teaserVideo, trackPosition.valueAsNumber, videoDuration);

        sendEvent({
            category: 'Profile',
            action: 'Teaser',
            label: 'Track position',
            value: trackPosition.valueAsNumber,
        });
    });

    muteBtn?.addEventListener('click', () => {
        const isMuted = teaserVideo.muted;
        if (isMuted) {
            teaserVideo.volume = mutedVolume;
        } else {
            mutedVolume = teaserVideo.volume;
            teaserVideo.volume = 0;
        }
        teaserVideo.muted = !isMuted;

        sendEvent({
            category: 'Profile',
            action: 'Teaser',
            label: isMuted ? 'Unmute' : 'Mute',
        });
    });

    volumeSlider?.addEventListener('input', () => {
        teaserVideo.muted = false; // unmute on volume change from user.
        const newVolume: number = volumeSlider.valueAsNumber / 100;
        teaserVideo.volume = newVolume;

        sendEvent({
            category: 'Profile',
            action: 'Teaser',
            label: 'Volume adjust',
            value: newVolume,
        });
    });
};

const playVideo = async (mediaControlsCnt: HTMLDivElement | null, teaserVideo: HTMLVideoElement, profileCnt: HTMLDivElement | null): Promise<void> => {
    mediaControlsCnt?.classList.add(IS_VISIBLE);
    teaserVideo.classList.add(IS_VISIBLE);
    profileCnt?.classList.add(MEDIA_PLAYING);
    teaserVideo.play();
};

const pauseVideo = (mediaControlsCnt: HTMLDivElement | null, teaserVideo: HTMLVideoElement, profileCnt: HTMLDivElement | null, playBtn: HTMLDivElement | null): void => {
    mediaControlsCnt?.classList.remove(IS_VISIBLE);
    playBtn?.classList.add(IS_ACTIVE);
    profileCnt?.classList.remove(MEDIA_PLAYING);
    teaserVideo.pause();
};

const resetVideo = (mediaControlsCnt: HTMLDivElement | null, teaserVideo: HTMLVideoElement, profileCnt: HTMLDivElement | null, playBtn: HTMLDivElement | null): void => {
    mediaControlsCnt?.classList.remove(IS_VISIBLE);
    teaserVideo.classList.remove(IS_VISIBLE);
    playBtn?.classList.add(IS_ACTIVE);
    profileCnt?.classList.remove(MEDIA_PLAYING);
    teaserVideo.pause();
    teaserVideo.currentTime = 0;
};

const scrollVideo = (teaserVideo: HTMLVideoElement, trackValue: number, videoDuration: number): void => {
    const styleTrack = document.querySelector<HTMLStyleElement>('#js-controls__track');
    const currentTime = (trackValue / 100) * videoDuration;
    const width = trackValue === 0 ? '1px' : trackValue + '%';
    const style = `input[type='range'].media-controls__track{background-size:${width}, 100%}`;

    if (styleTrack) {
        styleTrack.textContent = style;
    }
    teaserVideo.currentTime = currentTime;
};

const updateVideoTrack = (completed: number, trackPosition: HTMLInputElement | null): void => {
    const styleTrack = document.querySelector<HTMLStyleElement>('#js-controls__track');
    const newValue = parseInt(`${completed}`, 10);
    const width = newValue === 0 ? '1px' : newValue + '%';
    const style = `input[type='range'].media-controls__track{background-size:${width}, 100%}`;

    if (styleTrack) {
        styleTrack.textContent = style;
    }
    if (trackPosition) {
        trackPosition.valueAsNumber = newValue;
    }
};

const fadeOutControls = (controls: (HTMLDivElement | null)[]): void => {
    controls.forEach((control: HTMLDivElement) => {
        control?.classList.remove(ANIMATION.FADE_IN_FAST);
        control?.classList.add(ANIMATION.FADE_OUT_FAST);
    });
};

const fadeInControls = (controls: (HTMLDivElement | null)[]): void => {
    controls.forEach((control: HTMLDivElement) => {
        control?.classList.remove(ANIMATION.FADE_OUT_FAST);
        control?.classList.add(ANIMATION.FADE_IN_FAST);
    });
};

const toggleControls = (mediaControlsCn: HTMLDivElement | null, controls: (HTMLDivElement | null)[]): void => {
    let timeout: ReturnType<typeof setTimeout>;

    fadeInControls(controls);
    timeout = setTimeout(() => {
        fadeOutControls(controls);
    }, 2500);

    mediaControlsCn?.addEventListener('mouseleave', () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
            fadeOutControls(controls);
        }, 2500);
    });

    mediaControlsCn?.addEventListener('mousemove', () => {
        clearTimeout(timeout);
        fadeInControls(controls);
    });
};

export default mediaControls;
